import * as React from 'react'
import logo from '../../assets/images/logo-header.png' // relative path to image
import {
	AppBar,
	Toolbar,
	Typography,
	Menu,
	MenuItem,
	Paper,
	Box,
	Divider,
	Badge,
	IconButton,
	Pagination,
	Button,
	Slider,
} from '@mui/material'
import './BrockAppBar.scss'
import { useTokenClaims } from '../../security/useTokenClaims'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import LogoutIcon from '@mui/icons-material/Logout'
import NotificationsIcon from '@mui/icons-material/Notifications'
import ExpandCircleDownIcon from '@mui/icons-material/ExpandCircleDown'
import RefreshIcon from '@mui/icons-material/Refresh'
import ZoomInIcon from '@mui/icons-material/ZoomIn'
import ZoomOutIcon from '@mui/icons-material/ZoomOut'
import { useMsal } from '@azure/msal-react'
import {
	listNotifications,
	markNotificationAsRead,
	markAllNotificationsAsRead,
} from '../../services/pushSubscriptionService'
import { useEffect } from 'react'
import { Notification } from '../../types/Core/Notification'
import { formatLocaleShortDateTime } from '../../services/CommonServices/DataFormatters'
import { useProjectFeaturesContext } from '../project/contexts/ProjectFeaturesContext'
import { useNavigate } from 'react-router-dom'
import SyncMenu from './SyncMenu'
import ConnectionIndicator from './ConnectionIndicator'
import { useZoom } from '../hooks/useZoom'
import PageHelp from '../trainingHelp/PageHelp'

type BrockAppBarProps = {
	showAccountInfo: boolean
}

/** Non-production hint from `window.location.hostname` (localhost, dev*, *-test*, etc.). */
function getHostEnvironmentLabel(hostname: string): 'Local' | 'Dev' | 'Test' | null {
	const host = hostname.toLowerCase()
	if (host === 'localhost' || host === '127.0.0.1') {
		return 'Local'
	}
	// Match test before dev so explicit test hosts win; avoid bare "dev" inside unrelated words via segment boundaries.
	if (host.startsWith('test.') || host.includes('.test.') || /(^|[.-])test($|[.-])/.test(host)) {
		return 'Test'
	}
	if (host.startsWith('dev.') || host.includes('.dev.') || /(^|[.-])dev($|[.-])/.test(host)) {
		return 'Dev'
	}
	return null
}

export default function BrockAppBar({ showAccountInfo = true }: BrockAppBarProps) {
	const { firstName, lastName, role, projectId } = useTokenClaims()
	const { projectTimeZone, projectTitle } = useProjectFeaturesContext()
	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
	const [notificationsAnchorEl, setNotificationsAnchorEl] = React.useState<null | HTMLElement>(null)
	const [selectedNotification, setSelectedNotification] = React.useState<Notification | null>(null)
	const [notificationDetailsAnchorEl, setNotificationDetailsAnchorEl] =
		React.useState<null | HTMLElement>(null)

	const profileMenuOpen = Boolean(anchorEl)
	const notificationsOpen = Boolean(notificationsAnchorEl)
	const { zoomLevel, setZoomLevel, zoomIn, zoomOut, resetZoom, zoomLevels } = useZoom()
	const notificationDetailsOpen = Boolean(notificationDetailsAnchorEl)
	const { instance } = useMsal()
	const [notifications, setNotifications] = React.useState<Notification[]>([])
	const [isRefreshing, setIsRefreshing] = React.useState(false)
	const [page, setPage] = React.useState(1)
	const itemsPerPage = 5
	const totalPages = Math.ceil(notifications.length / itemsPerPage)
	const navigate = useNavigate()
	const hostEnvironmentLabel = React.useMemo(
		() => getHostEnvironmentLabel(window.location.hostname),
		[],
	)

	const handleClick = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget)
	}

	const handleClose = () => {
		setAnchorEl(null)
	}

	const handleLogout = () => {
		handleClose()
		// Clear local storage
		localStorage.removeItem('wpa-token-storage')
		// Logout from Azure AD
		instance.logoutRedirect({
			postLogoutRedirectUri: window.location.origin,
		})
	}

	const handleNotificationsClick = (event: React.MouseEvent<HTMLElement>) => {
		setNotificationsAnchorEl(event.currentTarget)
	}

	const handleNotificationsClose = () => {
		setNotificationsAnchorEl(null)
	}

	const handleDetailClick = (event: React.MouseEvent<HTMLElement>, notification: Notification) => {
		event.stopPropagation()
		setSelectedNotification(notification)
		setNotificationDetailsAnchorEl(event.currentTarget)
		if (!notification.read) {
			markNotificationAsRead(notification.id)
			setNotifications(
				notifications.map((n) => (n.id === notification.id ? { ...n, read: true } : n)),
			)
		}
	}

	const handleMarkAllAsRead = () => {
		markAllNotificationsAsRead().then(() => {
			setNotifications(notifications.map((n) => ({ ...n, read: true })))
		})
	}

	const handleDetailsClose = () => {
		setNotificationDetailsAnchorEl(null)
		setSelectedNotification(null)
	}

	const handleRefresh = () => {
		setIsRefreshing(true)
		listNotifications()
			.then((data) => {
				setNotifications(data)
			})
			.finally(() => {
				setIsRefreshing(false)
			})
	}

	const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
		setPage(value)
	}

	const paginatedNotifications = notifications.slice((page - 1) * itemsPerPage, page * itemsPerPage)

	const handleNotificationNavigate = (event: React.MouseEvent<HTMLElement>) => {
		event.stopPropagation()
		if (selectedNotification?.url) {
			const [path, queryString] = selectedNotification.url.split('?')
			const queryParams = queryString.split('&').reduce((acc, param) => {
				const [key, value] = param.split('=')
				return { ...acc, [key]: value }
			}, {})
			navigate(path, {
				state: { ...queryParams, fromNotification: true, key: Date.now() },
				replace: true,
			})
		}
	}

	useEffect(() => {
		if (showAccountInfo) {
			listNotifications().then((data) => {
				setNotifications(data)
			})
		}
	}, [showAccountInfo])

	return (
		<AppBar
			position='fixed'
			sx={{
				backgroundColor: (theme) => theme.palette.primary.main,
				zIndex: (theme) => theme.zIndex.drawer + 1,
			}}
		>
			<Toolbar sx={{ justifyContent: 'space-between' }}>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
					<Typography variant='h6' noWrap component='div'>
						<img src={logo} alt={'Brock Logo'} height={50} />
					</Typography>
					{(projectTitle || (projectId && projectId > 0)) && (
						<Typography
							variant='h1'
							noWrap
							component='h1'
							sx={{
								ml: 1.5,
								color: 'inherit',
								overflow: 'hidden',
								textOverflow: 'ellipsis',
								maxWidth: 350,
							}}
						>
							{projectTitle && projectTitle}
							{projectTitle && projectId && projectId > 0 && ' · '}
							{projectId && projectId > 0 && `#${projectId}`}
						</Typography>
					)}
				</Box>
				<div
					style={{
						display: 'flex',
						flexDirection: 'row',
						alignItems: 'center',
						justifyContent: 'center',
					}}
				>
					<Typography variant='h1' noWrap component='h1'>
						Field Management Tool v2.0 {hostEnvironmentLabel && `(${hostEnvironmentLabel})`}
					</Typography>
					<Typography variant='h3' noWrap component='h3'>
						<div
							style={{
								display: 'flex',
								flexDirection: 'row',
								alignItems: 'center',
								justifyContent: 'center',
								gap: '8px',
							}}
						>
							{showAccountInfo && (
								<>
									<SyncMenu showAccountInfo={showAccountInfo} projectTimeZone={projectTimeZone} />
									{/* Notifications Menu */}
									<div
										onClick={handleNotificationsClick}
										style={{
											display: 'flex',
											flexDirection: 'row',
											alignItems: 'center',
											justifyContent: 'center',
											cursor: 'pointer',
											marginLeft: '10px',
										}}
									>
										<Badge
											badgeContent={notifications.filter((n) => !n.read).length}
											color='error'
											anchorOrigin={{
												vertical: 'top',
												horizontal: 'left',
											}}
											overlap='circular'
											sx={{
												'& .MuiBadge-badge': {
													left: 0,
													top: '50%',
													transform: 'translate(-50%, -50%)',
												},
											}}
										>
											<NotificationsIcon />
										</Badge>
									</div>
									<Menu
										anchorEl={notificationsAnchorEl}
										open={notificationsOpen}
										onClose={handleNotificationsClose}
										transformOrigin={{ horizontal: 'right', vertical: 'top' }}
										anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
										PaperProps={{
											elevation: 0,
											sx: {
												overflow: 'visible',
												filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
												mt: 1.5,
												'&:before': {
													content: '""',
													display: 'block',
													position: 'absolute',
													top: 0,
													right: 14,
													width: 10,
													height: 10,
													bgcolor: 'background.paper',
													transform: 'translateY(-50%) rotate(45deg)',
													zIndex: 0,
												},
											},
										}}
									>
										<Paper sx={{ width: 320, maxWidth: '100%' }}>
											<Box
												sx={{
													p: 2,
													display: 'flex',
													flexDirection: 'column',
													gap: 1,
												}}
											>
												<Box
													sx={{
														display: 'flex',
														justifyContent: 'space-between',
														alignItems: 'center',
													}}
												>
													<Typography variant='h6' sx={{ fontWeight: 'bold' }}>
														Notifications
													</Typography>

													<IconButton
														size='medium'
														onClick={(e) => {
															e.stopPropagation()
															handleRefresh()
														}}
														disabled={isRefreshing}
													>
														<RefreshIcon
															fontSize='medium'
															sx={{
																animation: isRefreshing ? 'spin 0.5s linear infinite' : 'none',
																'@keyframes spin': {
																	'0%': {
																		transform: 'rotate(0deg)',
																	},
																	'100%': {
																		transform: 'rotate(360deg)',
																	},
																},
															}}
														/>
													</IconButton>
												</Box>
												{notifications.some((n) => !n.read) && (
													<Button
														variant='outlined'
														size='small'
														fullWidth
														sx={{ color: 'primary.main', fontSize: '0.75rem' }}
														onClick={(e) => {
															e.stopPropagation()
															handleMarkAllAsRead()
														}}
													>
														Mark all as read
													</Button>
												)}
											</Box>
											<Divider />
											{notifications.length === 0 && (
												<MenuItem>
													<Typography variant='body1'>No notifications</Typography>
												</MenuItem>
											)}
											{paginatedNotifications.map((notification) => (
												<MenuItem
													key={notification.id}
													onClick={(e) => handleDetailClick(e, notification)}
													sx={{
														py: 1.5,
														display: 'flex',
														justifyContent: 'space-between',
														alignItems: 'center',
														backgroundColor: notification.read ? 'inherit' : 'action.hover',
														borderLeft: notification.read ? 'none' : '4px solid',
														borderLeftColor: 'primary.main',
														'&:hover': {
															backgroundColor: notification.read
																? 'action.hover'
																: 'action.selected',
														},
													}}
												>
													<Box>
														<Typography
															variant='body1'
															sx={{
																overflow: 'hidden',
																textOverflow: 'ellipsis',
																whiteSpace: 'nowrap',
																maxWidth: '250px',
																fontWeight: notification.read ? 'normal' : 'bold',
															}}
														>
															{notification.title}
														</Typography>
														<Typography
															variant='caption'
															color='text.secondary'
															sx={{
																fontWeight: notification.read ? 'normal' : 'medium',
															}}
														>
															{projectTimeZone &&
																formatLocaleShortDateTime(notification.createdAt, projectTimeZone)}
														</Typography>
													</Box>
													<ExpandCircleDownIcon
														fontSize='small'
														color='action'
														sx={{ transform: 'rotate(-90deg)' }}
													/>
												</MenuItem>
											))}
											{totalPages > 1 && (
												<Box sx={{ p: 1, display: 'flex', justifyContent: 'center' }}>
													<Pagination
														count={totalPages}
														page={page}
														onChange={handlePageChange}
														size='medium'
														sx={{
															'& .MuiPaginationItem-root': {
																fontSize: '0.75rem',
															},
														}}
													/>
												</Box>
											)}
										</Paper>
									</Menu>
									<Menu
										anchorEl={notificationDetailsAnchorEl}
										open={notificationDetailsOpen}
										onClose={handleDetailsClose}
										anchorOrigin={{
											horizontal: 'left',
											vertical: 'top',
										}}
										transformOrigin={{
											horizontal: 'right',
											vertical: 'top',
										}}
										PaperProps={{
											elevation: 0,
											sx: {
												overflow: 'visible',
												filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
												mt: 1.5,
												'&:before': {
													content: '""',
													display: 'block',
													position: 'absolute',
													top: 20,
													right: -5,
													width: 10,
													height: 10,
													bgcolor: 'background.paper',
													transform: 'translateY(-50%) rotate(45deg)',
													zIndex: 0,
												},
											},
										}}
									>
										<Paper sx={{ width: 400, maxWidth: '100%' }}>
											<Box sx={{ p: 2 }}>
												<Typography
													variant='h6'
													sx={{
														fontWeight: 'bold',
														cursor: 'pointer',
														textDecoration: 'underline',
														'&:hover': {
															color: 'primary.main',
														},
													}}
													onClick={handleNotificationNavigate}
												>
													{selectedNotification?.title}
												</Typography>
												<Typography variant='caption' color='text.secondary'>
													{projectTimeZone &&
														selectedNotification &&
														formatLocaleShortDateTime(
															selectedNotification.createdAt,
															projectTimeZone,
														)}
												</Typography>
											</Box>
											<Divider />
											<Box sx={{ p: 2 }}>
												<Typography variant='body1' sx={{ whiteSpace: 'pre-wrap' }}>
													{selectedNotification?.message}
												</Typography>
											</Box>
										</Paper>
									</Menu>
									<div
										onClick={handleClick}
										style={{
											display: 'flex',
											flexDirection: 'row',
											alignItems: 'center',
											justifyContent: 'center',
											cursor: 'pointer',
										}}
									>
										<div>
											{firstName} {lastName.substring(0, 1)}
										</div>
										<div style={{ paddingLeft: '5px', paddingTop: '5px' }}>
											<AccountCircleIcon />
										</div>
									</div>
									<Menu
										anchorEl={anchorEl}
										open={profileMenuOpen}
										onClose={handleClose}
										onClick={handleClose}
										transformOrigin={{ horizontal: 'right', vertical: 'top' }}
										anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
										PaperProps={{
											elevation: 0,
											sx: {
												overflow: 'visible',
												filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
												mt: 1.5,
												'&:before': {
													content: '""',
													display: 'block',
													position: 'absolute',
													top: 0,
													right: 14,
													width: 10,
													height: 10,
													bgcolor: 'background.paper',
													transform: 'translateY(-50%) rotate(45deg)',
													zIndex: 0,
												},
											},
										}}
									>
										<Paper sx={{ width: 220, maxWidth: '100%' }}>
											<Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
												<AccountCircleIcon sx={{ fontSize: 40 }} />
												<Box>
													<Typography variant='subtitle1' sx={{ fontWeight: 'bold' }}>
														{firstName} {lastName}
													</Typography>
													<Typography variant='body2' color='text.secondary'>
														{role.title.replace(/([A-Z])/g, ' $1').trim()}
													</Typography>
												</Box>
											</Box>
											<Divider />
											<Box
												onClick={(e) => e.stopPropagation()}
												sx={{
													mx: 1.5,
													mb: 2,
													mt: 1.5,
													px: 2,
													py: 2,
													borderRadius: 2,
													bgcolor: 'action.hover',
													border: '1px solid',
													borderColor: 'divider',
												}}
											>
												<Typography
													variant='caption'
													color='text.secondary'
													sx={{
														display: 'block',
														mb: 1.5,
														fontWeight: 600,
														textTransform: 'uppercase',
														letterSpacing: '0.05em',
													}}
												>
													Zoom Level
												</Typography>
												<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
													<IconButton
														size='small'
														onClick={zoomOut}
														aria-label='Decrease text size'
														sx={{
															p: 0.75,
															bgcolor: 'background.paper',
															boxShadow: 1,
															'&:hover': { bgcolor: 'action.selected' },
														}}
													>
														<ZoomOutIcon fontSize='small' />
													</IconButton>
													<Slider
														value={Math.max(
															0,
															zoomLevels.indexOf(zoomLevel as (typeof zoomLevels)[number]),
														)}
														min={0}
														max={zoomLevels.length - 1}
														step={1}
														onChange={(_, value) => {
															const level = zoomLevels[value as number]
															if (level !== undefined) {
																setZoomLevel(level)
															}
														}}
														sx={{
															flex: 1,
															py: 0,
															'& .MuiSlider-thumb': { width: 18, height: 18 },
															'& .MuiSlider-rail': { opacity: 0.5 },
														}}
														valueLabelDisplay='auto'
														valueLabelFormat={() => `${zoomLevel}%`}
													/>
													<IconButton
														size='small'
														onClick={zoomIn}
														aria-label='Increase text size'
														sx={{
															p: 0.75,
															bgcolor: 'background.paper',
															boxShadow: 1,
															'&:hover': { bgcolor: 'action.selected' },
														}}
													>
														<ZoomInIcon fontSize='small' />
													</IconButton>
												</Box>
												<Box
													sx={{
														display: 'flex',
														justifyContent: 'space-between',
														alignItems: 'center',
														mt: 1,
													}}
												>
													<Typography variant='caption' color='text.secondary'>
														{zoomLevel}%
													</Typography>
													{zoomLevel !== 100 && (
														<Button
															size='small'
															onClick={resetZoom}
															sx={{
																minWidth: 0,
																px: 1.5,
																fontSize: '0.7rem',
																textTransform: 'none',
																color: 'primary.main',
															}}
														>
															Reset to 100%
														</Button>
													)}
												</Box>
											</Box>
											<Divider sx={{ my: 0 }} />
											<Box sx={{ py: 0.5 }} />
											<MenuItem
												onClick={handleLogout}
												sx={{
													py: 1.5,
													color: 'error.main',
													mx: 1,
													mb: 1,
													borderRadius: 1,
													'&:hover': {
														bgcolor: 'error.light',
														color: 'error.contrastText',
													},
												}}
											>
												<LogoutIcon sx={{ mr: 2 }} />
												Logout
											</MenuItem>
										</Paper>
									</Menu>
								</>
							)}
							{showAccountInfo && <PageHelp />}
							<ConnectionIndicator />
						</div>
					</Typography>
				</div>
			</Toolbar>
		</AppBar>
	)
}
